export const dpaData = {
  en: {
    title: "Data Processing Agreement (DPA) – English Version",
    version: "Version: 1.0",
    acceptance: "Acceptance: Checking the acceptance box constitutes full and binding acceptance of this Data Processing Agreement (DPA).",
    content: `
1. Purpose
This Data Processing Agreement ("DPA") defines the terms under which digi-bakery.com processes personal data on behalf of the Client (the Baker) as part of the order-management and gift-card services.

2. Party Roles
	•	The Client is the Data Controller.
	•	digi-bakery.com is the Data Processor.

3. Description of Processing
	•	Data types: name, email, phone number, order history, gift-card information.
	•	Data subjects: customers of the Baker.
	•	Purpose: provision of the SaaS platform.
	•	Retention: duration of the service + legal retention period.

4. Processor Obligations
digi-bakery.com agrees to:
	1	Process data only on documented instructions from the Client.
	2	Implement appropriate technical and organizational security measures.
	3	Ensure confidentiality of personnel with access to data.
	4	Assist the Client with GDPR/Privacy requests (access, deletion, correction).
	5	Notify the Client of any data breach within a reasonable timeframe.
	6	Delete or return the data at the end of the service agreement.

5. Sub-processing
The Client authorizes the use of technical sub-processors (hosting, email delivery, payment providers). The list is available in the Privacy Policy.

6. International Data Transfers
If any data is transferred outside the Client's country, digi-bakery.com ensures appropriate safeguards (Standard Contractual Clauses, etc.).

7. Security Measures
The Processor applies industry-standard measures: HTTPS, encryption, access control, logging.

8. Client Rights
The Client may at any time:
	•	request an audit,
	•	obtain the list of sub-processors,
	•	request deletion or return of data.

9. Term & Termination
This DPA remains valid for the duration of the Client's use of the service.

10. Acceptance via Checkbox
By checking the "I accept the DPA" box, the Client acknowledges having read and accepted this Agreement.
    `
  },
  fr: {
    title: "Accord de Sous-traitance des Données (DPA) – Version Française",
    version: "Version : 1.0",
    acceptance: "Acceptation : la validation d'une case à cocher vaut acceptation complète du présent Accord de Sous-traitance (DPA).",
    content: `
1. Objet
Le présent Accord de Sous-traitance (« DPA ») définit les conditions dans lesquelles digi-bakery.com traite des données personnelles pour le compte du Client (le Boulanger) dans le cadre de la fourniture du service de gestion de commandes et de cartes-cadeaux.

2. Rôles des parties
	•	Le Client est le Responsable du traitement.
	•	digi-bakery.com est le Sous-traitant.

3. Description du traitement
	•	Catégories de données : nom, email, téléphone, historique de commandes, informations de cartes-cadeaux.
	•	Personnes concernées : clients du Boulanger.
	•	Finalités : fourniture de la plateforme SaaS.
	•	Durée de conservation : durée d'utilisation du service + durée légale.

4. Obligations du Sous-traitant
digi-bakery.com s'engage à :
	1	Traiter les données uniquement sur instruction documentée du Client.
	2	Mettre en œuvre des mesures techniques et organisationnelles appropriées.
	3	Garantir la confidentialité du personnel ayant accès aux données.
	4	Aider le Client à répondre aux demandes RGPD (accès, suppression, rectification).
	5	Notifier toute violation de données dans un délai raisonnable.
	6	Supprimer ou restituer les données à la fin du contrat.

5. Sous-traitance ultérieure
Le Client autorise l'utilisation de sous-traitants techniques (hébergement, envoi d'emails, paiements). La liste est publiée dans la Politique de Confidentialité.

6. Transferts internationaux
En cas de transfert de données hors du pays du Client, digi-bakery.com garantit l'utilisation de garanties appropriées (Clauses Contractuelles Types, etc.).

7. Sécurité
Le Sous-traitant applique des mesures conformes aux normes du secteur : HTTPS, chiffrement, contrôle d'accès, journalisation.

8. Droits du Client
Le Client peut à tout moment :
	•	demander un audit,
	•	obtenir la liste des sous-traitants,
	•	demander la suppression ou récupération des données.

9. Durée et résiliation
Le présent DPA reste valide tant que le Client utilise le service.

10. Acceptation via case à cocher
En cochant la case « J'accepte le DPA », le Client confirme avoir lu et accepté la version actuelle du présent Accord.
    `
  }
};

export const privacyPolicyData = {
  en: {
    title: "Privacy Policy — digi-bakery.com",
    effectiveDate: "Effective date: 04 November 2025",
    content: `
1. Introduction
digi-bakery.com ("Company", "we", "us") provides a management platform for micro-bakers and an online ordering interface for their customers (the "Service"). We respect your privacy and are committed to protecting personal information. This Privacy Policy explains how we collect, use, disclose, and protect personal information.

2. Our Role: Distinction between Platform and Baker
To ensure clarity regarding data responsibility:
o When you are a Baker: We act as a Data Controller regarding your account information.
o When you are a Customer of a Baker: We act as a Data Processor (Service Provider) acting on behalf of the Baker. The Baker is the Data Controller. We collect and store your data solely to enable the Baker to manage their business and fulfill your orders. The Baker has full access to the data you provide to their shop.

3. Information We Collect
We collect the following personal information depending on your usage:
o For Bakers: Name, business details, email address, payment information.
o For Customers: Name, email address, phone number, residential/delivery address (optional; provided by you for order fulfillment).

4. How We Collect Information
We collect information you provide directly when you create an account, place an order on a baker’s shop, submit forms, contact support, or otherwise interact with the Service.

5. Cookies and Tracking Technologies
Our Service uses cookies strictly necessary for the proper functioning of the application. We do not use marketing, advertising, or third-party analytics cookies.

o Session Cookies: To maintain your authentication and keep you logged in.
o Security Cookies: To detect and prevent fraud or abuse.
o Technical Cookies: Essential for load balancing and interface customization (e.g., language preference).

Note regarding Consent (EU/GDPR): As these cookies are strictly necessary for the delivery of the Service requested by the user, they are exempt from the requirement of prior consent.

6. Purposes of Processing
We use personal information to:
o Provide, operate, and maintain the Service for Bakers and Customers.
o Allow Bakers to process orders and manage their customer database.
o Communicate with you (transactional emails, account notices, support).
o Ensure the security and integrity of the platform.
o Comply with legal obligations.

7. Legal Bases for Processing (EU/EEA)
If you are in the EU/EEA, we process personal data based on:
o Performance of a contract: To provide the Service to Bakers and process orders for Customers.
o Legitimate interests: Security, fraud prevention, and technical improvements.
o Legal obligations: Compliance with tax and accounting laws.

8. Sharing and Disclosure
We may share personal information with:
o The Baker: If you are a Customer, your data is shared with the Baker you are ordering from so they can fulfill your request.
o Service Providers: Sub-processors who help operate the Service (hosting, email delivery, payment processing).
o Legal Authorities: If required by law or to protect rights and safety.
o Business Transfers: In the event of a merger, sale, or reorganization.

9. International Transfers
Your information may be stored and processed in countries other than your country of residence, including the United States. When data is transferred from the EU/EEA or the UK to other countries, we use appropriate safeguards (e.g., Standard Contractual Clauses) to protect your data.

10. Retention and Anonymization
We retain personal information only as long as necessary to fulfill the purposes described or as required by law.
o Operational Data: Retained while the account is active or as long as the Baker requires it for business history.
o Legal Archiving: Certain data may be kept for longer periods (e.g., 5 to 10 years) to comply with tax and accounting obligations.
o Purging: Once data is no longer needed for legal or operational purposes, it is either securely deleted or anonymized/pseudonymized for statistical analysis purposes.

11. Security
We implement reasonable administrative, technical, and physical safeguards designed to protect personal information (encryption, access controls). However, no method of transmission over the Internet is 100% secure.

12. Your Rights
Depending on your jurisdiction (GDPR, CCPA, PIPEDA), you may have rights including:
o Access & Correction: Request a copy or correction of your data.
o Deletion: Request deletion of your personal data (subject to legal retention requirements).
o Portability: Receive your data in a structured format.
o Objection: Object to certain processing activities.

Specific Note for Customers: If you wish to exercise your rights regarding data held by a specific Baker, please contact the Baker directly or contact us, and we will facilitate the request.

13. Children
Our Service is not intended for children under 16. We do not knowingly collect personal information from children.

14. Local Privacy Notices
o California (CCPA/CPRA): We do not sell personal information. Consumers have the right to request disclosure and deletion.
o Canada (PIPEDA): Residents may request access and correction in accordance with applicable rules.
o Europe (GDPR): Residents have the rights listed in Section 12 and may lodge a complaint with a DPA.

15. Changes to this Policy
We may update this Privacy Policy. We will post the revised policy with an updated effective date.

16. Contact Us
For questions or to exercise your rights:
digi-bakery.com
Email: support@digi-bakery.com

17. Shop Visitor Analytics (Anonymized)
When you visit a baker's public shop page, digi-bakery.com collects the following aggregated, non-personal statistics on behalf of the baker:
	•	Total page loads and estimated unique sessions (based on session-scoped browser storage — no cookie, no identifier persisted across sessions)
	•	Device type: mobile, desktop, or tablet (inferred from browser user-agent string)
	•	Browser language preference (e.g., "en-US", "fr-CA") — as a regional proxy only; not linked to any individual
	•	Referrer category: Direct, Search engine, Social media, or Other
	•	Hour of visit (UTC) for peak-hours analysis
	•	Product add-to-cart events (count only, not linked to any user)

What is NOT collected: IP addresses, cookies, device fingerprints, names, emails, or any identifier that could be linked to a specific individual. No consent banner is required because these are fully aggregated, anonymous metrics that do not constitute personal data under GDPR Article 4(1) or PIPEDA.

The baker uses this data solely to understand their shop's traffic patterns. Data is retained in aggregated daily summaries for up to 12 months.
    `
  },
  fr: {
    title: "Politique de confidentialité — digi-bakery.com",
    effectiveDate: "Date d'entrée en vigueur : 04 Novembre 2025",
    content: `
1. Introduction
digi-bakery.com (« Société », « nous ») fournit une plateforme de gestion pour les micro-boulangers ainsi qu'une boutique en ligne pour leurs clients (le « Service »). Nous respectons votre vie privée et nous nous engageons à protéger les renseignements personnels. Cette politique explique comment nous collectons, utilisons et protégeons vos données.

2. Notre Rôle : Distinction entre la Plateforme et le Boulanger
Afin de clarifier les responsabilités en matière de données :
o Si vous êtes un Boulanger : Nous agissons en tant que Responsable de Traitement concernant les informations de votre compte.
o Si vous êtes Client d'un Boulanger : Nous agissons en tant que Sous-traitant (Prestataire de services) pour le compte du Boulanger. Le Boulanger est le Responsable de Traitement. Nous collectons et stockons vos données uniquement pour permettre au Boulanger de gérer son activité et vos commandes. Le Boulanger a un accès complet aux données que vous fournissez à sa boutique.

3. Renseignements que nous recueillons
Nous recueillons les renseignements suivants selon votre utilisation :
o Pour les Boulangers : Nom, informations commerciales, courriel, données de paiement.
o Pour les Clients : Nom, adresse courriel, numéro de téléphone, adresse résidentielle/livraison (facultative ; fournie pour la commande).

4. Comment nous recueillons les renseignements
Nous recueillons les informations que vous fournissez directement lors de la création d'un compte, lors d'une commande sur la boutique d'un boulanger, via des formulaires ou lors de contacts avec le support.

5. Cookies et Technologies de Suivi
Notre Service utilise uniquement des cookies strictement nécessaires au bon fonctionnement de l'application. Nous n'utilisons aucun cookie publicitaire, marketing ou d'analyse tierce.

o Cookies de session : Pour maintenir votre authentification et votre connexion active.
o Cookies de sécurité : Pour la protection contre la fraude et les abus.
o Cookies techniques : Nécessaires à l'équilibrage de charge et aux préférences d'interface (ex: langue).

Note sur le Consentement (RGPD/UE) : Ces cookies étant strictement nécessaires à la fourniture du Service demandé par l'utilisateur, ils sont exemptés du recueil de consentement préalable (bandeau cookies).

6. Finalités du traitement
Nous utilisons les renseignements personnels pour :
o Fournir, exploiter et maintenir le Service pour les Boulangers et les Clients.
o Permettre aux Boulangers de traiter les commandes et gérer leur base clients.
o Communiquer avec vous (notifications transactionnelles, mises à jour).
o Assurer la sécurité et l'intégrité de la plateforme.
o Respecter les obligations légales.

7. Bases juridiques du traitement (UE/EEE)
Si vous êtes dans l'UE/EEE, nous traitons vos données sur la base de :
o Exécution d'un contrat : Fournir le Service aux Boulangers et traiter les commandes des Clients.
o Intérêts légitimes : Sécurité, prévention de la fraude et amélioration technique.
o Obligations légales : Conformité fiscale et comptable.

8. Partage et divulgation
Nous pouvons partager les renseignements personnels avec :
o Le Boulanger : Si vous êtes Client, vos données sont partagées avec le Boulanger chez qui vous commandez pour qu'il puisse honorer la commande.
o Prestataires de services : Sous-traitants techniques aidant à l'exploitation du Service (hébergement, envoi d'emails, paiement).
o Autorités légales : Si la loi l'exige ou pour protéger des droits et la sécurité.
o Transfert d'entreprise : En cas de fusion, vente ou réorganisation.

9. Transferts internationaux
Vos renseignements peuvent être stockés et traités dans des pays autres que votre pays de résidence (y compris les États-Unis). Lors de transferts hors UE/EEE ou Royaume-Uni, nous utilisons des garanties appropriées (ex: Clauses Contractuelles Types) pour protéger vos données.

10. Conservation et Anonymisation
Nous conservons les données aussi longtemps que nécessaire pour les finalités décrites ou requises par la loi.
o Données opérationnelles : Conservées tant que le compte est actif ou que le Boulanger en a besoin pour son historique commercial.
o Archivage légal : Certaines données sont conservées plus longtemps (ex: 5 à 10 ans) pour répondre aux obligations fiscales et comptables.
o Purge : Lorsque les données ne sont plus nécessaires, elles sont soit supprimées de manière sécurisée, soit anonymisées/pseudonymisées à des fins statistiques.

11. Sécurité
Nous mettons en œuvre des mesures administratives, techniques et physiques raisonnables pour protéger les données. Toutefois, aucune méthode de transmission sur Internet n'est totalement sécurisée.

12. Vos droits
Selon votre juridiction (RGPD, CCPA, LPRPDE), vous disposez de droits incluant :
o Accès et Rectification : Demander une copie ou la correction de vos données.
o Suppression : Demander l'effacement de vos données (sous réserve d'obligations légales).
o Portabilité : Recevoir vos données dans un format structuré.
o Opposition : Vous opposer à certains traitements.

Note spécifique pour les Clients : Si vous souhaitez exercer vos droits concernant des données détenues par un Boulanger spécifique, veuillez le contacter directement ou nous contacter pour que nous facilitions la demande.

13. Enfants
Notre Service n'est pas destiné aux enfants de moins de 16 ans. Nous ne collectons pas sciemment de données d'enfants.

14. Avis locaux (Californie, Canada, Europe)
o Californie (CCPA/CPRA) : Nous ne vendons pas de renseignements personnels.
o Canada (LPRPDE) : Les résidents peuvent demander l'accès et la rectification selon les règles applicables.
o Europe (RGPD) : Les résidents peuvent exercer les droits listés en Section 12 et déposer plainte auprès d'une autorité de protection (ex: CNIL).

15. Modifications de la politique
Nous pouvons mettre à jour cette politique. Nous publierons la version révisée avec une nouvelle date d'entrée en vigueur.

16. Nous contacter
Pour toute question ou demande :
digi-bakery.com
Courriel : support@digi-bakery.com

17. Analytiques visiteurs de la boutique (Anonymisées)
Lorsque vous visitez la boutique publique d'un boulanger, digi-bakery.com collecte les statistiques agrégées et non personnelles suivantes pour le compte du boulanger :
	•	Nombre total de pages chargées et sessions estimées uniques (basé sur le stockage de session du navigateur — pas de cookie, pas d'identifiant conservé entre les sessions)
	•	Type d'appareil : mobile, ordinateur ou tablette (déduit de l'agent utilisateur du navigateur)
	•	Langue du navigateur (ex. « fr-CA », « en-US ») — utilisée comme indicateur régional uniquement ; non rattachée à un individu
	•	Catégorie de provenance : Direct, Moteur de recherche, Réseau social, ou Autre
	•	Heure de visite (UTC) pour l'analyse des pics d'activité
	•	Ajouts au panier par produit (comptage uniquement, non rattaché à un utilisateur)

Ce qui N'est PAS collecté : adresses IP, cookies, empreintes numériques, noms, adresses e-mail ni aucun identifiant pouvant être rattaché à un individu. Aucune bannière de consentement n'est requise car ces métriques sont entièrement agrégées et anonymes, et ne constituent pas des données personnelles au sens de l'article 4(1) du RGPD ni de la LPRPDE.

Le boulanger utilise ces données uniquement pour comprendre les tendances de trafic de sa boutique. Les données sont conservées sous forme de résumés journaliers agrégés pendant 12 mois maximum.
    `
  }
};